import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { submitPayment } from "@/lib/payment";
import { toast } from "sonner";
import { Lock, Mail, Minus, Plus, ShieldCheck, Gift, Loader2 } from "lucide-react";

const euros = (c) => `${((c || 0) / 100).toFixed(((c || 0) % 100) ? 2 : 0)} €`;

// Tableau de commande : cérémonie (déjà payée ou non) + messages (à l'unité ou par lot) -> prépaiement.
export const LivingOrderDialog = ({ open, onOpenChange, returnPath = "/postinity/preparer", alreadyCeremony = false, onPaid }) => {
  const { t } = useTranslation();
  const [prices, setPrices] = useState(null);
  const [wantCeremony, setWantCeremony] = useState(!alreadyCeremony);
  const [qty, setQty] = useState(1);
  const [bundleId, setBundleId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then((r) => setPrices(r.data.living_prices || null)).catch(() => setPrices(null));
    setWantCeremony(!alreadyCeremony);
  }, [open, alreadyCeremony]);

  const cerEff = prices?.ceremony_effective ?? 2900;
  const cerBase = prices?.ceremony_base ?? cerEff;
  const msgEff = prices?.message_effective ?? 500;
  const msgBase = prices?.message_base ?? msgEff;
  const bundles = prices?.bundles || [];
  const cerPromo = prices?.ceremony_promo_active;
  const msgPromo = prices?.message_promo_active;

  const selectedBundle = useMemo(() => bundles.find((b) => b.id === bundleId) || null, [bundles, bundleId]);
  const messagesCost = selectedBundle ? (selectedBundle.price_cents || 0) : qty * msgEff;
  const messagesCount = selectedBundle ? (selectedBundle.qty || 0) + (selectedBundle.bonus || 0) : qty;
  const total = (wantCeremony ? cerEff : 0) + messagesCost;
  const canPay = (wantCeremony || messagesCount > 0) && total >= 0;

  const pay = async () => {
    setBusy(true);
    try {
      const body = { ceremony: wantCeremony, origin_url: window.location.origin, return_path: returnPath };
      if (bundleId) body.bundle_id = bundleId; else body.messages_qty = qty;
      const r = await api.post("/living-candle/prepay/checkout", body);
      if (["free", "simulated", "already"].includes(r.data.mode)) {
        toast.success(t("po.order_success"));
        onPaid && onPaid();
        onOpenChange(false);
      } else {
        submitPayment(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("po.order_error"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass max-h-[90vh] overflow-y-auto border-white/10 sm:max-w-lg" data-testid="living-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg"><ShieldCheck size={18} className="text-sky-300" /> {t("po.order_title")}</DialogTitle>
        </DialogHeader>

        {!prices ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-sky-400" /></div>
        ) : (
        <div className="space-y-4">
          {/* Cérémonie */}
          <label className={`flex items-center justify-between gap-3 rounded-2xl border p-4 transition-colors ${alreadyCeremony ? "border-emerald-400/40 bg-emerald-500/10" : wantCeremony ? "border-sky-400/50 bg-sky-500/10" : "border-white/15 bg-white/[0.03]"}`} data-testid="order-ceremony-row">
            <div className="flex items-center gap-3">
              {!alreadyCeremony && (
                <input type="checkbox" checked={wantCeremony} onChange={(e) => setWantCeremony(e.target.checked)} className="h-5 w-5 accent-sky-500" data-testid="order-ceremony-check" />
              )}
              <div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-white"><Lock size={14} /> {t("po.card_ceremony")}</div>
                <p className="text-xs text-white/60">{t("po.order_ceremony_desc")}</p>
              </div>
            </div>
            <div className="text-right">
              {alreadyCeremony ? (
                <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 text-xs font-medium text-emerald-300">{t("po.order_already_paid")}</span>
              ) : (
                <div>
                  {cerPromo && <div className="text-xs text-white/40 line-through">{euros(cerBase)}</div>}
                  <div className={`text-lg font-bold ${cerPromo ? "text-amber-300" : "text-white"}`} data-testid="order-ceremony-price">{euros(cerEff)}</div>
                </div>
              )}
            </div>
          </label>

          {/* Messages à l'unité */}
          <div className={`rounded-2xl border p-4 ${!bundleId ? "border-sky-400/40 bg-sky-500/[0.06]" : "border-white/15 bg-white/[0.03]"}`} data-testid="order-messages-row">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-white"><Mail size={14} /> {t("po.order_messages")}</div>
                <p className="text-xs text-white/60">
                  {msgPromo && <span className="mr-1 text-white/40 line-through">{euros(msgBase)}</span>}
                  <span className={msgPromo ? "text-amber-300" : ""}>{euros(msgEff)}</span> {t("po.order_per_message")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setBundleId(null); setQty((q) => Math.max(0, q - 1)); }} className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10" data-testid="order-qty-minus"><Minus size={14} /></button>
                <span className="w-8 text-center text-lg font-bold text-white" data-testid="order-qty-value">{bundleId ? 0 : qty}</span>
                <button type="button" onClick={() => { setBundleId(null); setQty((q) => q + 1); }} className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10" data-testid="order-qty-plus"><Plus size={14} /></button>
              </div>
            </div>
          </div>

          {/* Lots promotionnels */}
          {bundles.length > 0 && (
            <div className="space-y-2" data-testid="order-bundles">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-300"><Gift size={13} /> {t("po.order_bundles")}</p>
              {bundles.map((b) => (
                <button key={b.id} type="button" onClick={() => setBundleId(bundleId === b.id ? null : b.id)}
                  className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition-colors ${bundleId === b.id ? "border-amber-400 bg-amber-500/15" : "border-white/15 bg-white/[0.03] hover:bg-white/[0.06]"}`}
                  data-testid={`order-bundle-${b.id}`}>
                  <span className="text-sm text-white">{b.label}{b.bonus ? <span className="ml-1 text-emerald-300">{t("po.order_bonus", { n: b.bonus })}</span> : null}</span>
                  <span className="text-sm font-bold text-amber-200">{euros(b.price_cents)}</span>
                </button>
              ))}
            </div>
          )}

          {/* Total */}
          <div className="flex items-center justify-between rounded-2xl border border-white/20 bg-black/30 px-4 py-3">
            <div>
              <span className="text-sm text-white/70">{t("po.order_total")}</span>
              {messagesCount > 0 && <p className="text-xs text-white/50">{t("po.order_summary", { n: messagesCount })}{wantCeremony ? t("po.order_plus_ceremony") : ""}</p>}
            </div>
            <span className="text-2xl font-bold text-white" data-testid="order-total">{euros(total)}</span>
          </div>
          <p className="text-center text-xs text-white/50">{t("po.order_note")}</p>
        </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10" onClick={() => onOpenChange(false)} data-testid="order-cancel-btn">{t("po.order_cancel")}</Button>
          <Button disabled={!canPay || busy} onClick={pay} className="rounded-full bg-sky-500 text-white hover:bg-sky-400" data-testid="order-pay-btn">
            {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <ShieldCheck size={16} className="mr-1.5" />} {t("po.order_pay")} {euros(total)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
